import { useSelector, useDispatch } from "react-redux";
import { Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Loader2 } from "lucide-react";
import { clearUser } from "./redux/UserSlice";
import { clearAllUserData, persistor } from "./redux/store";
import LandingPage from "./pages/LandingPage";
import Signin from "./pages/Auth/Signin";
import Signup from "./pages/Auth/Signup";
import VerifyOtp from "./pages/Auth/VerifyOtp";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import PasswordReset from "./pages/Auth/PasswordReset";
import { useEffect, useState } from "react";
import Dashboard from "./pages/Vendors/Dashboard";
import Pricing from "./pages/Vendors/Subscription/Pricing";
import Offers from "./pages/Vendors/Deals/Offers";
import NewDealForm from "./pages/Vendors/Deals/NewDealForm";
import EditDealForm from "./pages/Vendors/Deals/EditDealForm";
import Sender from "./pages/Vendors/Senders/Sender";
import CampaignOverview from "./pages/Vendors/Campaigns/CampaignOverview";
import Campaigns from "./pages/Vendors/Campaigns/Campaigns";
import CreateCampaignDeal from "./pages/Vendors/Campaigns/CreateCampaignDeal";
import BlastCampaign from "./pages/Vendors/Campaigns/BlastCampaign";
import RedemptionOverview from "./pages/Vendors/Deals/RedemptionOverview";
import "react-toastify/dist/ReactToastify.css";
import Notification from "./pages/Vendors/Notification";
import Support from "./pages/Vendors/Support";
import Subscription from "./pages/Vendors/Subscription/Subscription";
import ProfileSettings from "./pages/Vendors/ProfileSettings";
import StoreBuilder from "./pages/Vendors/StoreBuilder/StoreBuilder";
import Roles from "./pages/Vendors/Roles/Roles";
import Reporting from "./pages/Vendors/Reporting";
import ProtectedRoute from "./components/ProtectedRoute";
import EditCampaignDeal from "./components/Campaigns/EditCampaignDeal";
import CampaignRedemptions from "./pages/Vendors/Campaigns/CampaignRedemptions";
import PaymentPage from "./pages/Payment/PaymentPage";
import PaymentSuccess from "./pages/Payment/PaymentSuccess";
import PaymentFailed from "./pages/Payment/PaymentFailed";
import VerifyPayment from "./pages/Payment/VerifyPayment";
import PaymentHistory from "./pages/PaymentHistory";
import GuestSupport from "./pages/Support";
import ContactSales from "./pages/ContactSales";
import Analytics from "./pages/Analytics";
import CreateDeal from "./pages/CreateDeal";
import HelpCenter from "./pages/HelpCenter";
import { OtpProtectedRoute } from "./components/OTPVerifyProtectedRoute";
import Partners from "./pages/Vendors/Partners/Partners";
import PartnerAdd from "./pages/Vendors/Partners/PartnerAdd";
import PartnerEdit from "./pages/Vendors/Partners/PartnerEdit";
import Branches from "./pages/Vendors/Branches/Branches";
import BranchAdd from "./pages/Vendors/Branches/BranchAdd";
import BranchEdit from "./pages/Vendors/Branches/BranchEdit";
import BranchDetails from "./pages/Vendors/Branches/BranchDetails";
import BlastDeal from "./pages/Vendors/Deals/BlastDeal";
import CreateRole from "./pages/Vendors/Roles/CreateRole";
import ManageRoles from "./pages/Vendors/Roles/ManageRoles";
import EditRole from "./pages/Vendors/Roles/EditRole";
import CreateTeamMember from "./pages/Vendors/teamMembers/CreateTeamMember";
import EditTeamMember from "./pages/Vendors/teamMembers/EditTeamMember";
import AffiliateApplications from "./pages/Vendors/Affiliates/AffiliateApplications";
import AffiliateDetails from "./pages/Vendors/Affiliates/AffiliateDetails";
import DealDetails from "./pages/Vendors/Deals/DealDetails";
import Orders from "./pages/Vendors/Orders/Orders";
import Inventory from "./pages/Vendors/Inventory/Inventory";
import { PermissionProvider } from "./context/PermissionProvider";
import RestrictedAccessGuard from "./components/Permissions/RestrictedAccessGuard";
import VendorShell from "./layouts/VendorShell";

function App() {
  const user = useSelector((state) => state?.user?.user) || null;
  const dispatch = useDispatch();
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isLoading) return;
    
    const token = localStorage.getItem('dealboxx_token');

    if (!token && user && (user.email_verified_at || user.phone_number_verified_at)) {
      console.log('No token found but verified user exists - clearing user data');
      clearAllUserData();
    }
  }, [user, isLoading]);

  useEffect(() => {
    if (persistor) {
      const unsubscribe = persistor.subscribe(() => {
        const { bootstrapped } = persistor.getState();
        if (bootstrapped) {
          console.log('Persistence bootstrapped, setting loading to false');
          setIsLoading(false);
          unsubscribe();
        }
      });
      
      const fallbackTimer = setTimeout(() => {
        console.log('Persistence fallback timer triggered');
        setIsLoading(false);
        unsubscribe();
      }, 2000);
      
      return () => {
        clearTimeout(fallbackTimer);
        unsubscribe();
      };
    } else {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      const verified = Boolean(user.email_verified_at || user.phone_number_verified_at);
      console.log('User verification status:', {
        user: user.email,
        email_verified_at: user.email_verified_at,
        phone_number_verified_at: user.phone_number_verified_at,
        isVerified: verified
      });
      setIsVerified(verified);
    } else {
      setIsVerified(false);
    }
  }, [user]);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // useIdleTimeout(30);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center w-screen h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  console.log('App routing state:', { user: !!user, isVerified, isLoading });
  
  if (!user && !isVerified) {
    console.log('Routing to landing page - no user and not verified');
    return (
      <div>
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={true}
          newestOnTop={true}
          closeOnClick
          pauseOnFocusLoss={false}
          draggable
          theme="colored"
          pauseOnHover={false}
        />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/home" element={<LandingPage />} />
          <Route path="/signin" element={<Signin />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/password-reset" element={<PasswordReset />} />
          <Route path="/support" element={<GuestSupport />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/createdeal" element={<CreateDeal />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    );
  }

  if (user && !isVerified) {
    console.log('Routing to verification page - user exists but not verified');
    return (
      <div>
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={true}
          newestOnTop={true}
          closeOnClick
          pauseOnFocusLoss={false}
          draggable
          theme="colored"
          pauseOnHover={false}
        />
        <Routes>
          <Route path="/verify-otp" element={<VerifyOtp />} />
          <Route path="*" element={<Navigate to="/verify-otp" replace />} />
        </Routes>
      </div>
    );
  }

  if (user && isVerified) {
    console.log('Routing to dashboard - user exists and is verified');
    return (
      <PermissionProvider>
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={true}
          newestOnTop={true}
          closeOnClick
          pauseOnFocusLoss={false}
          draggable
          theme="colored"
          pauseOnHover={false}
        />
        <Routes>
          <Route
            element={
              <VendorShell
                isMobile={isMobile}
                isSidebarCollapsed={isSidebarCollapsed}
                setIsSidebarCollapsed={setIsSidebarCollapsed}
                isMobileMenuOpen={isMobileMenuOpen}
                setIsMobileMenuOpen={setIsMobileMenuOpen}
              />
            }
          >
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/verify-otp"
                  element={
                    <OtpProtectedRoute>
                      <VerifyOtp />
                    </OtpProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pricing"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <Pricing />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                <Route
                  path="/offers"
                  element={
                    <ProtectedRoute requirePermissions={true}>
                      <Offers setSelectedData={setSelectedData} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/offers/new"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <NewDealForm />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                <Route
                  path="/offers/edit/:slug?"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <EditDealForm />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                <Route
                  path="/partners"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <Partners />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                <Route
                  path="/add-partner"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <PartnerAdd />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                <Route
                  path="/edit-partner/:partner_slug"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <PartnerEdit />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                <Route
                  path="/branches"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <Branches />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                <Route
                  path="/add-branch"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <BranchAdd />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                <Route
                  path="/edit-branch/:branch_slug"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <BranchEdit />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                <Route
                  path="/branch-details/:branchSlug"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <BranchDetails />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                <Route
                  path="/affiliate-applications"
                  element={
                    <ProtectedRoute>
                      <AffiliateApplications />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/affiliate-details/:affiliateCode"
                  element={
                    <ProtectedRoute>
                      <AffiliateDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/deal-details/:dealSlug"
                  element={
                    <ProtectedRoute>
                      <DealDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/campaigns/senders"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <Sender />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                <Route
                  path="/deal/campaigns"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <Campaigns />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                <Route
                  path="/deal/campaigns/create/:slug?"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <CreateCampaignDeal />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                <Route
                  path="/deal/campaigns/edit/:slug"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <EditCampaignDeal />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                <Route
                  path="/campaigns/overview"
                  element={
                    <ProtectedRoute>
                      <CampaignOverview />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/deal/campaigns/blast/:campaign_slug"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <BlastCampaign />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                 <Route
                    path="/deal/blast/:deal_slug"
                    element={
                      <RestrictedAccessGuard>
                        <ProtectedRoute requirePermissions={true}>
                          <BlastDeal />
                        </ProtectedRoute>
                      </RestrictedAccessGuard>
                    }
                  />

                <Route
                  path="/redeem-offers"
                  element={
                    <ProtectedRoute requirePermissions={true}>
                      <RedemptionOverview />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/orders"
                  element={
                    <ProtectedRoute>
                      <Orders />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/inventory"
                  element={
                    <ProtectedRoute requirePermissions={true}>
                      <Inventory />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/redeem-campaigns"
                  element={
                    <ProtectedRoute requirePermissions={true}>
                      <CampaignRedemptions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notification"
                  element={
                    <ProtectedRoute>
                      <Notification />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/help-and-support"
                  element={
                    <ProtectedRoute>
                      <Support />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/subscription"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <Subscription />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                <Route
                  path="/profile-settings"
                  element={
                    <ProtectedRoute>
                      <ProfileSettings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-store"
                  element={
                    <ProtectedRoute>
                      <StoreBuilder />
                    </ProtectedRoute>
                  }
                />

                {/* ========== ROLES & PERMISSIONS ROUTES ========== */}
                
                {/* Main Roles Overview Page */}
                <Route
                  path="/roles-and-permissions"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <Roles />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                
                {/* Legacy route redirect for old /roles */}
                <Route
                  path="/roles"
                  element={<Navigate to="/roles-and-permissions" replace />}
                />

                {/* Role Management Routes */}
                <Route
                  path="/roles/manage"
                  element={
                    <ProtectedRoute requirePermissions={true}>
                      <ManageRoles />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/roles/create"
                  element={
                    <ProtectedRoute requirePermissions={true}>
                      <CreateRole />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/roles/edit/:roleId"
                  element={
                    <ProtectedRoute requirePermissions={true}>
                      <EditRole />
                    </ProtectedRoute>
                  }
                />

                {/* Team Member Management Routes */}
                <Route
                  path="/roles/team-member/create"
                  element={
                    <ProtectedRoute requirePermissions={true}>
                      <CreateTeamMember />
                    </ProtectedRoute>
                  }
                />
                
                <Route
                  path="/roles/team-member/edit/:memberId"
                  element={
                    <ProtectedRoute requirePermissions={true}>
                      <EditTeamMember />
                    </ProtectedRoute>
                  }
                />

                {/* ========== END ROLES & PERMISSIONS ROUTES ========== */}
                
                <Route
                  path="/reporting-and-analytics"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <Reporting />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                <Route
                  path="/payment"
                  element={
                    <ProtectedRoute>
                      <PaymentPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment/success"
                  element={
                    <ProtectedRoute>
                      <PaymentSuccess />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment/failed"
                  element={
                    <ProtectedRoute>
                      <PaymentFailed />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment/verify"
                  element={
                    <ProtectedRoute>
                      <VerifyPayment />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/payment-history"
                  element={
                    <RestrictedAccessGuard>
                      <ProtectedRoute requirePermissions={true}>
                        <PaymentHistory />
                      </ProtectedRoute>
                    </RestrictedAccessGuard>
                  }
                />
                <Route
                  path="/contact-sales"
                  element={
                    <ProtectedRoute>
                      <ContactSales />
                    </ProtectedRoute>
                  }
                />
          </Route>
        </Routes>
      </PermissionProvider>
    );
  }
}

export default App;
